import {
  Checkbox,
  CheckboxProps,
  createStyles,
  makeStyles,
  MenuItem,
  Paper,
  PaperProps,
  TextField,
  Theme,
} from '@material-ui/core';
import { Autocomplete, AutocompleteProps, AutocompleteRenderInputParams } from '@material-ui/lab';
import React, { forwardRef } from 'react';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    selectAllBtn: { color: theme.palette.primary.main, height: 60 },
    dropdownFullWidth: { width: '310px' },
  }),
);

export type MultiSelectAutocompleteProps = Omit<AutocompleteProps<any, true, true | false, any>, 'renderInput'> & {
  label: string;
  limitTags?: number;
  enableSelectAll?: boolean;
  requiredTextfield?: boolean;
  error?: boolean;
  errorText?: string;
  PaperProps?: PaperProps;
  CheckboxProps?: CheckboxProps;
  fullWidthDropdown?: boolean;
  renderInput?: (params: AutocompleteRenderInputParams) => React.ReactNode;
};

export const MultiSelectAutocomplete = forwardRef(
  (
    {
      label,
      options,
      requiredTextfield,
      enableSelectAll,
      limitTags,
      onChange,
      renderOption,
      ChipProps,
      PaperProps,
      CheckboxProps,
      value,
      error,
      errorText,
      fullWidthDropdown = false,
      renderInput = undefined,
      ...autocompleteProps
    }: MultiSelectAutocompleteProps,
    ref,
  ) => {
    const classes = useStyles();

    const isAllSelected = options.length === value?.length;
    // eslint-disable-next-line react/no-unstable-nested-components
    const CustomPaper = (props: any) => {
      const { children } = props;

      const handleOnSelectAll = (event: React.BaseSyntheticEvent<any>) => {
        event.preventDefault();
        onChange?.(event, isAllSelected ? [] : options, 'select-option');
      };

      return (
        <Paper {...PaperProps} className={fullWidthDropdown ? classes.dropdownFullWidth : ''}>
          {children}
          <MenuItem button className={classes.selectAllBtn} onMouseDown={handleOnSelectAll}>
            Select All
          </MenuItem>
        </Paper>
      );
    };

    const defaultRenderInput = (param: AutocompleteRenderInputParams): React.ReactNode => {
      return <TextField {...param} required={requiredTextfield} label={label} error={error} helperText={errorText} />;
    };

    return (
      <Autocomplete
        ref={ref}
        multiple
        limitTags={limitTags || 3}
        autoComplete
        disableListWrap
        autoHighlight
        disableCloseOnSelect
        options={options}
        PaperComponent={enableSelectAll ? CustomPaper : undefined}
        renderOption={(option, state) => {
          return (
            <>
              <Checkbox color={'primary'} size="small" checked={state.selected} {...CheckboxProps} />
              {renderOption ? renderOption(option, state) : option}
            </>
          );
        }}
        {...autocompleteProps}
        value={value}
        onChange={onChange}
        renderInput={renderInput ?? defaultRenderInput}
        ChipProps={{ onDelete: undefined, size: 'small', ...ChipProps }}
        classes={{
          clearIndicator: 'multiselect-autocomplete',
        }}
      />
    );
  },
);

MultiSelectAutocomplete.displayName = 'MultiSelectAutocomplete';

export default MultiSelectAutocomplete;
